import {environment} from '../../../environments/environment';
import {Injectable, Injector} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {UserSessionModelService} from '../Models/Users/user-session-model.service';
import {UserSessionDto} from '../../Models/User/user-session.interface';
import {ToastManagerService} from '../Platform/Ionic/toast-manager.service';
import {ApiStatusEnum} from '../../Shared/Enum/api-status.enum';

export type HttpMethod = 'get' | 'post' | 'patch' | 'put' | 'delete';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseApiService {

  protected user: UserSessionDto | null;

  protected baseUrl: string = environment.baseUrl;
  protected apiBaseUrl: string = '';

  public http: HttpClient;
  public userSession: UserSessionModelService;
  public toastCtrl: ToastManagerService;
  protected translate: TranslateService;

  protected constructor(
    protected injector: Injector
  ) {
    this.http = this.injector.get<HttpClient>(HttpClient);
    this.userSession = this.injector.get<UserSessionModelService>(UserSessionModelService);
    this.toastCtrl = this.injector.get<ToastManagerService>(ToastManagerService);
    this.translate = this.injector.get<TranslateService>(TranslateService);

    this.user = this.userSession.get();
  }


  /**
   * Hacer una llamada http
   *
   */
  public httpCall(endpoint: string, params: any = null, method: HttpMethod = 'post', oldApi: boolean = false): Observable<ApiResponse> {
    this.setBaseUrl(oldApi);
    return this.makeHttpCall(endpoint, params, method);
  }


  /**
   * Hacer una llamada http con una url base
   *
   */
  public httpBaseCall(endpoint: string, params: any = null, method: HttpMethod = 'post', apiUrl: string = ''): Observable<ApiResponse> {
    this.apiBaseUrl = apiUrl || this.baseUrl;
    return this.makeHttpCall(endpoint, params, method);
  }


  /**
   * Ejecuta una petición HTTP
   *
   */
  makeHttpCall(endpoint: string, params: any = null, method: HttpMethod = 'post'): Observable<ApiResponse> {
    switch (method) {
      case 'get':
        return this.getHttpCall(endpoint, params);

      case 'post':
        return this.postHttpCall(endpoint, params);

      case 'patch':
        return this.patchHttpCall(endpoint, params);

      case 'put':
        return this.putHttpCall(endpoint, params);

      case 'delete':
        return this.deleteHttpCall(endpoint, params);

      default:
        console.warn(`Unknown HTTP method received: ${method}`);
        break;
    }

    return this.getHttpCall(endpoint, params); // Use GET request as a default callback
  }


  /**
   * Llamada http tipo 'post'
   *
   */
  private postHttpCall(endpoint: string, params: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.apiBaseUrl + endpoint, params)
      .pipe(catchError((error) => this.handleError(error)));
  }


  /**
   * Llamada http tipo 'put'
   *
   */
  private putHttpCall(endpoint: string, params: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(this.apiBaseUrl + endpoint, params)
      .pipe(catchError((error) => this.handleError(error)));
  }


  /**
   * Llamada http tipo 'patch'
   *
   */
  private patchHttpCall(endpoint: string, params?: any): Observable<ApiResponse> {
    return this.http.patch<ApiResponse>(this.apiBaseUrl + endpoint, params)
      .pipe(catchError((error) => this.handleError(error)));
  }


  /**
   * Llamada http tipo 'delete'
   *
   */
  private deleteHttpCall(endpoint: string, params?: any): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(this.apiBaseUrl + endpoint, {params})
      .pipe(catchError((error) => this.handleError(error)));
  }


  /**
   * Llamada http tipo 'get'
   *
   */
  private getHttpCall(endpoint: string, params?: any): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.apiBaseUrl + endpoint, {params})
      .pipe(catchError((error) => this.handleError(error)));
  }


  /**
   * Asignar la ruta de la url
   *
   */
  private setBaseUrl(oldApi: boolean) {

    this.user = this.userSession.get();

    if (!(this.user && 'id' in this.user)) {
      this.apiBaseUrl = this.baseUrl;
      return;
    }

    if (oldApi) {
      this.apiBaseUrl = this.user.api;
      return;
    }

    this.apiBaseUrl = this.user.new_api;
  }


  /**
   * Manejar errores HTTP
   *
   */
  private handleError(httpError: HttpErrorResponse): Observable<never> {
    const httpMessage: string = httpError?.error?.message;
    const isClientError: boolean = httpError.status >= 400 && httpError.status < 500;

    const message: string = isClientError && httpMessage ? httpMessage : this.translate.instant('SERVER.ERROR_MESSAGE');
    this.toastCtrl.showErrorToast(message);

    return throwError(() => new Error(httpError.message));
  }

}

export interface ApiResponse {
  data: any;
  status: number | ApiStatusEnum;
  message?: string;
  mensaje?: string;
  duracion?: number;
}
